import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Modal, DataTable, toast } from "@/pages/app/kit";

export default function MatchingPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [params, setParams] = useSearchParams();
  const cropId = params.get("crop") || "";
  const [crops, setCrops] = useState([]);
  const [data, setData] = useState(null);
  const [approved, setApproved] = useState([]);
  const [modal, setModal] = useState(null);
  const [aForm, setAForm] = useState({ quantity: 0, channel: "fresh", price_per_unit: 0 });
  const [busy, setBusy] = useState(false);

  const loadApproved = () => api.get("/matches").then((r) => setApproved(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/crops").then((r) => setCrops(r.data)).catch(() => {});
    loadApproved();
  }, []);
  useEffect(() => {
    if (cropId) api.get(`/matches/suggest/${cropId}`).then((r) => setData(r.data)).catch(() => setData(null));
    else setData(null);
  }, [cropId]);

  const openApprove = (s) => { setModal(s); setAForm({ quantity: s.quantity_needed || 0, channel: "fresh", price_per_unit: s.target_price || 0 }); };
  const approve = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/matches/approve", { crop_id: cropId, demand_id: modal.demand_id, quantity: parseFloat(aForm.quantity)||0, channel: aForm.channel, price_per_unit: parseFloat(aForm.price_per_unit)||0 });
      toast.success(L("Dopasowanie zatwierdzone","Match approved")); setModal(null);
      api.get(`/matches/suggest/${cropId}`).then((r) => setData(r.data)); loadApproved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const apprCols = [
    { key: "crop_product", label: "Crop" },
    { key: "buyer_name", label: "Buyer" },
    { key: "quantity", label: "Qty", render: (r) => `${r.quantity.toLocaleString()} kg` },
    { key: "channel", label: "Channel" },
    { key: "price_per_unit", label: "Price", render: (r) => `${r.price_per_unit} PLN` },
    { key: "approved_by_name", label: "Approved by" },
  ];

  return (
    <div>
      <PageTitle title={t("dashboard.matching")} subtitle={L("Przejrzyste dopasowania zatwierdzane przez człowieka","Transparent, human-approved matching")} />
      <div className="mb-6 max-w-md">
        <Field label={L("Wybierz plon","Select crop")}>
          <Select value={cropId} onChange={(e) => setParams(e.target.value ? { crop: e.target.value } : {})} data-testid="match-crop-select">
            <option value="">—</option>
            {crops.map((c) => <option key={c.id} value={c.id}>{c.product} — {c.variety} ({c.risk?.remaining?.toLocaleString()} kg {L("pozostało","remaining")})</option>)}
          </Select>
        </Field>
      </div>

      {data && (
        <Panel title={`${L("Sugestie dla","Suggestions for")} ${data.crop.product} · ${L("pozostało","remaining")} ${data.remaining.toLocaleString()} kg`} className="mb-6">
          <div className="space-y-3">
            {data.suggestions.length === 0 && <p className="text-mutedtext">{t("empty")}</p>}
            {data.suggestions.map((s) => (
              <div key={s.demand_id} className="rounded-md border border-subtle p-4" data-testid={`suggestion-${s.demand_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-charcoal">{s.buyer_name} — {s.buyer_product} ({s.buyer_grade})</div>
                    <p className="mt-1 text-sm text-mutedtext">{s.explanation}</p>
                    <div className="mt-1 text-xs text-mutedtext">{L("Potrzeba","Needs")}: {s.quantity_needed?.toLocaleString()} kg · target {s.target_price} PLN</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded bg-forest/10 px-2 py-1 text-xs font-bold text-forest">score {s.score}</span>
                    <Btn onClick={() => openApprove(s)} data-testid={`approve-${s.demand_id}`}>{t("cta.approve")}</Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title={L("Zatwierdzone dopasowania","Approved matches")}>
        <DataTable columns={apprCols} rows={approved} empty={t("empty")} testid="approved-matches-table" />
      </Panel>

      <Modal open={!!modal} onClose={() => setModal(null)} title={t("cta.approve")}>
        {modal && (
          <form onSubmit={approve} className="space-y-4">
            <p className="text-sm text-mutedtext">{modal.buyer_name}</p>
            <Field label={L("Ilość (kg)","Quantity (kg)")} required><Input type="number" value={aForm.quantity} onChange={(e) => setAForm({ ...aForm, quantity: e.target.value })} /></Field>
            <Field label={L("Kanał","Channel")}><Select value={aForm.channel} onChange={(e) => setAForm({ ...aForm, channel: e.target.value })}><option value="fresh">Fresh</option><option value="processing">Processing</option></Select></Field>
            <Field label={L("Cena / jedn.","Price / unit")}><Input type="number" step="0.01" value={aForm.price_per_unit} onChange={(e) => setAForm({ ...aForm, price_per_unit: e.target.value })} /></Field>
            <Btn type="submit" disabled={busy}>{busy?"…":t("cta.approve")}</Btn>
          </form>
        )}
      </Modal>
    </div>
  );
}
